import axios from 'axios';
import { v1 as uuidv1 } from 'uuid';
import ErrorHandler from './services/errorHandler';
import TokenManager from './services/tokenManager';

// Debug environment variable
const baseURL = process.env.REACT_APP_BACKEND_API_BASE_URL_SKAI_ONE;
const axiosConfig = axios.create({
  baseURL,
  timeout: 30000, // 30 seconds timeout
});

let isRefreshing = false;
let failedQueue = [];
let unauthorizedDispatched = false;

export const resetUnauthorizedFlag = () => { unauthorizedDispatched = false; };

const dispatchUnauthorized = () => {
  if (unauthorizedDispatched) return;
  unauthorizedDispatched = true;
  window.dispatchEvent(new Event('auth:unauthorized'));
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor
axiosConfig.interceptors.request.use(
  (config) => {
    // Add common headers
    config.headers = {
      'Content-Type': 'application/json',
      'x-request-id': uuidv1(),
      'x-lang': localStorage.getItem('skai1Language') || 'en',
      ...config.headers,
    };

    // Add auth token if available, but don't override existing Authorization header
    const token = localStorage.getItem('skai1AccessToken');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosConfig.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      if (error.response?.data?.code) {
        return error.response;
      }
      // Skip toast for aborted requests (caused by navigation after logout)
      if (!axios.isCancel(error) && error.code !== 'ERR_CANCELED') {
        ErrorHandler.handle(error, { showToast: true });
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosConfig(originalRequest);
      }).catch(err => Promise.reject(err));
    }

    const currentToken = TokenManager.getAccessToken();
    if (!currentToken) {
      dispatchUnauthorized();
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Use raw axios (not axiosConfig) so this request bypasses this interceptor
      const refreshResponse = await axios.get(`${baseURL}/api/v1/account/refresh-token`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'x-lang': localStorage.getItem('skai1Language') || 'en',
          'x-request-id': uuidv1(),
        },
      });

      const newToken = refreshResponse.data?.data?.token;
      if (!newToken) throw new Error('No token in refresh response');

      TokenManager.setAccessToken(newToken);
      localStorage.setItem('userData', JSON.stringify(refreshResponse.data.data));

      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosConfig(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      dispatchUnauthorized();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);



export default axiosConfig;